import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Container,
  Paper,
  Stack,
  useTheme,
  useMediaQuery,
  Fab,
  Tooltip,
  Button,
} from '@mui/material';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import BadgeIcon from '@mui/icons-material/Badge';
import { snapshotIsVendor, snapshotIsVisitor } from '../utils/employeeTypeUi';
import {
  unlockWebviewAudioPlayback,
  createAttendanceVoicePlayer,
  attachFirstGestureWebviewAudioUnlock,
  buildWebviewWebSocketUrl,
  buildWebviewFetchHeaders,
  parseCameraIdsFromSearch,
  extractEntryCameraId,
  matchesWebviewCamera,
  fetchCameraTypeById,
  appendCameraIdQueryParams,
  resolveCompanyLogoSrc,
  getCompanyLogoSrc,
} from '../utils/webviewAudio';
import { keyframes } from '@emotion/react';

// API endpoints + websocket helpers
const normalizeBaseUrl = (value) => {
  if (!value) return '';
  return value.endsWith('/') ? value.slice(0, -1) : value;
};

const resolveApiBase = () => {
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    const origin = window.location.origin;
    const url = new URL(origin);
    // In production (HTTPS), use same origin (reverse proxy handles routing)
    // In development (HTTP), use port 8000 for the backend API
    if (url.protocol === 'https:') {
      return normalizeBaseUrl(origin);
    }
    // Development: backend is on port 8000
    return normalizeBaseUrl(`${url.protocol}//${url.hostname}:8000`);
  }
  return normalizeBaseUrl('http://localhost:8000');
};
const API_BASE_URL = resolveApiBase();
// Display duration per card (9 seconds)
const DISPLAY_DURATION = 9000;
const SIDEBAR_LIMIT = parseInt(process.env.REACT_APP_SIDEBAR_LIMIT || '7', 10);
const HISTORY_PAGE_SIZE = SIDEBAR_LIMIT;

const pad2 = (value) => value.toString().padStart(2, '0');

const normalizeTimestampString = (value) => {
  if (!value) return null;
  let normalized = value.trim();
  if (!normalized) return null;
  // Replace space between date/time with T for ISO parsing
  if (normalized.includes(' ') && !normalized.includes('T')) {
    normalized = normalized.replace(' ', 'T');
  }
  // Ensure timezone info present; default to UTC if missing
  if (!/(z|Z|[+-]\d{2}:?\d{2})$/.test(normalized)) {
    normalized = `${normalized}${normalized.includes('T') ? '' : 'T'}${normalized.includes('T') ? '' : '00:00:00'}Z`;
  }
  // Trim microseconds to milliseconds for Date parsing
  const dotIndex = normalized.indexOf('.');
  if (dotIndex !== -1) {
    const zoneMatch = normalized.slice(dotIndex).match(/(Z|[+-]\d{2}:?\d{2})$/);
    const zone = zoneMatch ? zoneMatch[0] : '';
    const fraction = normalized.slice(dotIndex + 1, zoneMatch ? -zone.length : undefined);
    const millis = fraction.slice(0, 3).padEnd(3, '0');
    const beforeDot = normalized.slice(0, dotIndex);
    normalized = `${beforeDot}.${millis}${zone}`;
  }
  return normalized;
};

const parseTimestampValue = (value) => {
  if (value === null || value === undefined) {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'number') {
    const date = new Date(value > 1e12 ? value : value * 1000);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (/^\d{10,}$/.test(trimmed)) {
      const num = Number(trimmed);
      const date = new Date(num > 1e12 ? num : num * 1000);
      return Number.isNaN(date.getTime()) ? null : date;
    }
    const normalized = normalizeTimestampString(trimmed);
    if (!normalized) return null;
    const parsed = new Date(normalized);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

const formatTimestampLabel = (rawValue, fallbackDate) => {
  const parsed = parseTimestampValue(rawValue) || fallbackDate;
  if (!(parsed instanceof Date) || Number.isNaN(parsed.getTime())) {
    return '';
  }
  // Date Parts
  const day = pad2(parsed.getDate());
  const month = pad2(parsed.getMonth() + 1);
  const year = parsed.getFullYear();

  // Time Parts (12-hour)
  const hours24 = parsed.getHours();
  const period = hours24 >= 12 ? 'PM' : 'AM';
  let hours12 = hours24 % 12;
  if (hours12 === 0) hours12 = 12;
  const hours = pad2(hours12);
  const minutes = pad2(parsed.getMinutes());

  // Final Format: TIME FIRST, THEN DATE
  return `${hours}:${minutes} ${period} · ${day}/${month}/${year}`;
};

// Animations
const float = keyframes`
  0%, 100% { transform: translate(0px, 0px) rotate(0deg); }
  33% { transform: translate(30px, -30px) rotate(120deg); }
  66% { transform: translate(-20px, 20px) rotate(240deg); }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.03); }
  100% { transform: scale(1); }
`;

const slideIn = keyframes`
  from { transform: translateY(30px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const connectionPulse = keyframes`
  0% { box-shadow: 0 0 0 0 currentColor; }
  70% { box-shadow: 0 0 0 8px rgba(0, 0, 0, 0); }
  100% { box-shadow: 0 0 0 0 rgba(0, 0, 0, 0); }
`;

const waveBar = keyframes`
  0%, 100% { transform: scaleY(0.35); }
  50% { transform: scaleY(1); }
`;

const rippleRing = keyframes`
  0%   { transform: scale(1);   opacity: 0.7; }
  100% { transform: scale(2.8); opacity: 0; }
`;

// Fallback avatar component
const toDataUrl = (value) => {
  if (!value) return null;
  if (value.startsWith('data:')) return value;
  return `data:image/jpeg;base64,${value}`;
};

const toLaneNumber = (raw) => {
  if (raw === null || raw === undefined || raw === '') return null;
  const parsed = parseInt(String(raw), 10);
  return Number.isNaN(parsed) || parsed <= 0 ? null : parsed;
};

const resolveLaneFromPathname = () => {
  if (typeof window === 'undefined' || !window.location) return null;
  const parts = String(window.location.pathname || '').split('/').filter(Boolean);
  const webviewIdx = parts.findIndex((p) => String(p).toLowerCase() === 'webview');
  if (webviewIdx < 0 || webviewIdx + 1 >= parts.length) return null;
  const rawPart = String(parts[webviewIdx + 1] || '');
  let decoded = rawPart;
  try {
    decoded = decodeURIComponent(rawPart);
  } catch (e) {
    decoded = rawPart;
  }
  const compact = decoded.toLowerCase().trim().replace(/\s+/g, '');
  const match = compact.match(/^lane(\d+)$/) || compact.match(/^lane-(\d+)$/) || compact.match(/^(\d+)$/);
  return match ? toLaneNumber(match[1]) : null;
};

const WebView = ({ laneNumber = null, cameraIds = null, embedded = false }) => {
  const location = useLocation();
  // preserved states
  const [currentEntry, setCurrentEntry] = useState(null);
  const [sidebarEntries, setSidebarEntries] = useState([]);

  // active main-screen cards (FIFO). newest at end.
  const [activeCards, setActiveCards] = useState([]);
  const [visibleHistoryCount, setVisibleHistoryCount] = useState(SIDEBAR_LIMIT);
  const [hasMoreHistory, setHasMoreHistory] = useState(true);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [cameraTypeById, setCameraTypeById] = useState({});

  // Company settings state - fetched from database (active company with is_active=true)
  const [companySettings, setCompanySettings] = useState(null);
  const [isSocketConnected, setIsSocketConnected] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isBelow1300 = useMediaQuery('(max-width:1300px)');
  const isBelow1100 = useMediaQuery('(max-width:1100px)');
  const isBelow800 = useMediaQuery('(max-width:800px)');
  const isShortScreen = useMediaQuery('(max-height:700px)');

  const websocketRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const dedupeTimersRef = useRef(new Map());
  const historyOffsetRef = useRef(0);
  const historyLoadingRef = useRef(false);

  // Map of per-card timers: id -> timeoutId
  const activeTimersRef = useRef(new Map());

  const resolvedLaneNumber = useMemo(() => {
    const fromProp = toLaneNumber(laneNumber);
    if (fromProp) return fromProp;
    return resolveLaneFromPathname();
  }, [laneNumber]);

  const cameraIdsPropKey = Array.isArray(cameraIds)
    ? cameraIds.map((id) => String(id || '').trim().toLowerCase()).filter(Boolean).join(',')
    : '';
  const resolvedCameraIds = useMemo(() => {
    const fromSearch = parseCameraIdsFromSearch(location?.search || '');
    if (fromSearch.length) return fromSearch;
    return cameraIdsPropKey ? cameraIdsPropKey.split(',') : [];
  }, [cameraIdsPropKey, location?.search]);

  const cameraFilterKey = resolvedCameraIds.join(',');

  useEffect(() => {
    if (!resolvedCameraIds.length) {
      setCameraTypeById({});
      return;
    }
    let cancelled = false;
    const loadCameraTypes = async () => {
      try {
        const map = await fetchCameraTypeById(API_BASE_URL);
        if (!cancelled) {
          console.log('[CUSTOM-WEBVIEW] camera types', map);
          setCameraTypeById(map);
        }
      } catch (err) {
        console.error('[CUSTOM-WEBVIEW] camera type load failed', err);
      }
    };
    loadCameraTypes();
    return () => {
      cancelled = true;
    };
  }, [cameraFilterKey, resolvedCameraIds.length]);

  const matchesLane = useCallback((entry) => {
    if (!resolvedLaneNumber) return true;
    return toLaneNumber(entry?.laneNumber) === resolvedLaneNumber;
  }, [resolvedLaneNumber]);

  const matchesCamera = useCallback((entry) => (
    matchesWebviewCamera(entry, resolvedCameraIds, cameraTypeById)
  ), [cameraTypeById, resolvedCameraIds]);

  // helper to determine attendance event
  const isAttendanceEvent = (payload = {}) => {
    const eventType = (payload.event || '').toString().toLowerCase();
    const mode = (payload.mode || '').toString().toLowerCase();
    return eventType === 'attendance' || mode === 'attendance';
  };

  const buildDisplayEntry = (payload = {}, overrides = {}) => {
    const person = payload.person || {};
    const timestampValue = overrides.timestamp || payload.timestamp || payload.created_at;
    const parsedTimestamp = parseTimestampValue(timestampValue);
    const safeDate = parsedTimestamp || new Date();
    const rawAction = (overrides.action || payload.action || payload.type || '').toString().toLowerCase();
    const action = rawAction.includes('out') ? 'check_out' : 'check_in';
    const rawMode = (overrides.mode || payload.mode || (payload.event === 'attendance' ? 'attendance' : 'normal') || '').toString().toLowerCase();
    const mode = rawMode === 'attendance' ? 'attendance' : 'normal';
    const camera = (overrides.camera || payload.camera || (action === 'check_out' ? 'OUT' : 'IN') || '').toString().toUpperCase();
    const trackId = overrides.trackId || payload.track_id || payload.trackId || payload.trackID || payload.trackid;
    const name = overrides.name || payload.person_name || payload.name || person.name || 'Unknown Visitor';
    const personId = overrides.personId || payload.person_id || payload.id || person.person_id || `guest-${Date.now()}`;
    const employeeCode = overrides.employee_code || payload.employee_code || person.employee_code || null;
    const designation = overrides.designation || payload.designation || person.designation || '';
    const department = overrides.department || payload.department || person.department || 'General';
    const employeeType =
      overrides.employeeType ?? payload.employee_type ?? person.employee_type ?? null;
    const image = toDataUrl(overrides.image || payload.image || person.image || null);
    const validFrom = overrides.validFrom || payload.valid_from || person.valid_from || null;
    const validTill = overrides.validTill || payload.valid_till || person.valid_till || null;
    const validityStatus = (
      overrides.validityStatus ||
      payload.validity_status ||
      person.validity_status ||
      null
    );

    const metadata = payload.request_metadata && typeof payload.request_metadata === 'object'
      ? payload.request_metadata
      : {};
    const entryLaneNumber = toLaneNumber(
      overrides.laneNumber ??
      payload.lane_number ??
      payload.laneNumber ??
      metadata.lane_number ??
      metadata.laneNumber
    );
    const entryCameraId = extractEntryCameraId(payload, overrides);

    const isValidityExpired = (validTillValue, statusValue) => {
      if (statusValue === 'expired') return true;
      if (statusValue && statusValue !== 'expired') return false;
      if (!validTillValue) return false; // unlimited/open-ended
      const end = new Date(validTillValue);
      if (Number.isNaN(end.getTime())) return false;
      return Date.now() > end.getTime();
    };
    return {
      id: payload.id || payload.attendance_id || `${personId}-${safeDate.getTime()}`,
      auditId: payload.audit_id || overrides.auditId,
      name,
      personId,
      employeeCode,
      trackId,
      action,
      mode,
      camera,
      image,
      designation,
      department,
      employeeType,
      validFrom,
      validTill,
      validityStatus,
      isValidityExpired: isValidityExpired(validTill, validityStatus),
isLateEntry: Boolean(
  overrides.isLateEntry ?? payload.is_late_entry ?? false
),
        timestamp: safeDate.toISOString(),
      time: formatTimestampLabel(timestampValue, safeDate),
      modeLabel: mode === 'attendance' ? 'Attendance Marked' : 'Normal Check-In',
      actionLabel: action === 'check_out' ? 'Checked Out' : 'Checked In',
      directionText: camera ? `${camera} Camera` : (action === 'check_out' ? 'Exit Event' : 'Entry Event'),
      laneNumber: entryLaneNumber,
      cameraId: entryCameraId,
      hasVoice: Boolean(payload.has_voice),
      voiceFetchId: payload.voice_fetch_id || null,
      voiceType:
        payload.voice_type === 'checkout'
          ? 'checkout'
          : payload.voice_type === 'checkin'
            ? 'checkin'
            : null,
      voiceFile: payload.voice_file && typeof payload.voice_file === 'object' ? payload.voice_file : null,
    };
  };

  const audioUnlockedRef = useRef(false);
  const voicePlayerRef = useRef(null);
  if (!voicePlayerRef.current) {
    voicePlayerRef.current = createAttendanceVoicePlayer({
      isUnblocked: () => audioUnlockedRef.current,
      onPlaybackStart: () => setVoicePlaying(true),
      onPlaybackEnd: () => setVoicePlaying(false),
    });
  }
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  const [voicePlaying, setVoicePlaying] = useState(false);

  const handleAudioUnlockClick = async () => {
    try {
      await unlockWebviewAudioPlayback();
      audioUnlockedRef.current = true;
      setAudioUnlocked(true);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    return attachFirstGestureWebviewAudioUnlock(() => {
      audioUnlockedRef.current = true;
      setAudioUnlocked(true);
    });
  }, []);

  const pushEntryToSidebar = useCallback((entry) => {
    if (!entry) return;

    setSidebarEntries(current => {
      // Remove duplicate with the same ID
      const filtered = current.filter(e => e.id !== entry.id);

      // Add new notification at top
      const updated = [entry, ...filtered];
      return updated.sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));
    });
  }, []);

  /** Card + voice dedupe: known person uses personId+action (not per-track). Guest/unknown uses track. */
  const getEntryKey = (entry) => {
    if (!entry) return null;
    if (entry.auditId) return `audit-${entry.auditId}`;
    const pid = entry.personId != null ? String(entry.personId).trim() : '';
    const guestOrUnknown = !pid || pid.startsWith('guest-');
    if (!guestOrUnknown) {
      return `${pid}-${entry.action}`;
    }
    const rawTrack = entry.trackId;
    if (rawTrack !== undefined && rawTrack !== null && String(rawTrack).trim() !== '') {
      return `track-${rawTrack}`;
    }
    if (pid) {
      return `${pid}-${entry.action}`;
    }
    return null;
  };

  // dedupe logic preserved
  const shouldDisplayEntry = (entry) => {
    const key = getEntryKey(entry);
    if (!key) return true;
    const existing = dedupeTimersRef.current.get(key);
    if (existing) {
      return false;
    }
    const timeoutId = setTimeout(() => {
      dedupeTimersRef.current.delete(key);
    }, DISPLAY_DURATION);
    dedupeTimersRef.current.set(key, timeoutId);
    return true;
  };

  // remove card helper (called on expiry or when popping due to >4)
  const removeCard = useCallback((id) => {
    setActiveCards(prev => {
      const card = prev.find(c => c.id === id);
      const filtered = prev.filter(c => c.id !== id);
      // push that card to sidebar as notification
      if (card) {
        pushEntryToSidebar(card.original || card);
      }
      // clear timer map
      const t = activeTimersRef.current.get(id);
      if (t) {
        clearTimeout(t);
      }
      activeTimersRef.current.delete(id);
      return filtered;
    });
  }, [pushEntryToSidebar]);

  // add or refresh card in activeCards (with its own timer)
  const addOrRefreshCard = useCallback((entry) => {
    if (!entry) return;
    setActiveCards(prev => {
      // check if already present
      const existing = prev.find(c => c.id === entry.id);
      if (existing) {
        // refresh timer: clear old timer and set a new one
        const oldTimer = activeTimersRef.current.get(entry.id);
        if (oldTimer) clearTimeout(oldTimer);
        const newTimeout = setTimeout(() => {
          removeCard(entry.id);
        }, DISPLAY_DURATION);
        activeTimersRef.current.set(entry.id, newTimeout);
        // update addedAt timestamp
        return prev.map(c => c.id === entry.id ? ({ ...c, addedAt: Date.now(), original: entry }) : c);
      }

      // Not present: if already 4 cards, remove the oldest (FIFO) and send to sidebar
      let next = [...prev];
      if (next.length >= 4) {
        const oldest = next.shift(); // remove first
        if (oldest) {
          // clear its timer and push to sidebar
          const oldT = activeTimersRef.current.get(oldest.id);
          if (oldT) clearTimeout(oldT);
          activeTimersRef.current.delete(oldest.id);
          pushEntryToSidebar(oldest.original || oldest);
        }
      }

      // add new card to the end (so newest at end)
      const wrapped = { ...entry, addedAt: Date.now(), original: entry };
      next.push(wrapped);

      // set timer for this card
      const timeoutId = setTimeout(() => {
        removeCard(wrapped.id);
      }, DISPLAY_DURATION);
      activeTimersRef.current.set(wrapped.id, timeoutId);

      return next;
    });
  }, [pushEntryToSidebar, removeCard]);

  // showEntry replaced/augmented to manage activeCards rather than single currentEntry only
  const showEntry = (entry) => {
    if (!entry) return;
    if (!shouldDisplayEntry(entry)) {
      return;
    }
    // add or refresh active card
    addOrRefreshCard(entry);
    if (
      entry.hasVoice &&
      entry.voiceFetchId &&
      entry.voiceType &&
      audioUnlockedRef.current &&
      voicePlayerRef.current
    ) {
      voicePlayerRef.current.enqueue({
        fetchId: entry.voiceFetchId,
        voiceType: entry.voiceType,
      });
    }
  };

  // whenever activeCards changes, keep currentEntry in-sync with the newest (last) card for backward compatibility
  useEffect(() => {
    if (activeCards.length === 0) {
      setCurrentEntry(null);
    } else {
      // show the latest added card as "currentEntry"
      const last = activeCards[activeCards.length - 1];
      setCurrentEntry(last.original || last);
    }
  }, [activeCards]);

  const mergeSidebarHistory = useCallback((entries = []) => {
    if (!Array.isArray(entries) || entries.length === 0) return;
    setSidebarEntries(current => {
      const byId = new Map();
      [...current, ...entries].forEach(entry => {
        if (!entry) return;
        const id = entry.id || entry.attendance_id || `${entry.personId || entry.name}-${entry.timestamp || entry.time}`;
        if (!byId.has(id)) {
          byId.set(id, entry);
        }
      });
      return Array.from(byId.values())
        .sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));
    });
  }, []);

  const loadSidebarHistory = useCallback(async ({ reset = false } = {}) => {
    if (historyLoadingRef.current) return;
    historyLoadingRef.current = true;
    setIsHistoryLoading(true);
    try {
      const offsetToLoad = reset ? 0 : historyOffsetRef.current;
      const params = new URLSearchParams({
        days: '30',
        limit: String(HISTORY_PAGE_SIZE),
        offset: String(offsetToLoad),
      });
      if (resolvedLaneNumber) {
        params.append('lane_number', String(resolvedLaneNumber));
      }
      appendCameraIdQueryParams(params, resolvedCameraIds);
      console.log('[CUSTOM-WEBVIEW] history fetch', {
        search: typeof window !== 'undefined' ? window.location.search : '',
        filterIds: resolvedCameraIds,
        offset: offsetToLoad,
      });

      const response = await fetch(
        `${API_BASE_URL}/api/v1/webview/attendance/history?${params.toString()}`,
        { headers: buildWebviewFetchHeaders() }
      );
      if (!response.ok) {
        setHasMoreHistory(false);
        return;
      }

      const payload = await response.json();
      const logs = Array.isArray(payload?.logs) ? payload.logs : [];
      const formatted = logs
        .filter(log => !log.is_missing_entry)
        .map(log => buildDisplayEntry(log, { mode: 'attendance' }))
        .filter(matchesLane)
        .filter(matchesCamera)
        .sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));
      console.log('[CUSTOM-WEBVIEW] history result', {
        raw: logs.length,
        afterFilter: formatted.length,
        rawCameraIds: logs.map((log) => log.camera_id || log.cameraId || null),
        rawCameras: logs.map((log) => log.camera || null),
      });

      if (reset) {
        setSidebarEntries(formatted);
        setVisibleHistoryCount(SIDEBAR_LIMIT);
      } else {
        mergeSidebarHistory(formatted);
      }

      const nextOffset = payload?.pagination?.next_offset ?? (
        offsetToLoad + (payload?.pagination?.limit || HISTORY_PAGE_SIZE)
      );
      historyOffsetRef.current = nextOffset;
      setHasMoreHistory(Boolean(payload?.pagination?.has_next));
    } catch (error) {
      console.error('[WebView] Error loading attendance history:', error);
      setHasMoreHistory(false);
    } finally {
      historyLoadingRef.current = false;
      setIsHistoryLoading(false);
    }
  }, [matchesCamera, matchesLane, mergeSidebarHistory, resolvedCameraIds, resolvedLaneNumber]);

  useEffect(() => {
    historyOffsetRef.current = 0;
    setHasMoreHistory(true);
    loadSidebarHistory({ reset: true });
  }, [loadSidebarHistory]);

  useEffect(() => {
    setActiveCards([]);
    setCurrentEntry(null);
    activeTimersRef.current.forEach((t) => clearTimeout(t));
    activeTimersRef.current.clear();
  }, [cameraFilterKey, resolvedLaneNumber]);

  const handleShowMoreHistory = async () => {
    const nextVisibleCount = visibleHistoryCount + SIDEBAR_LIMIT;
    if (hasMoreHistory && sidebarEntries.length < nextVisibleCount) {
      await loadSidebarHistory();
    }
    setVisibleHistoryCount(nextVisibleCount);
  };

  // cleanup timers on unmount
  useEffect(() => {
    return () => {
      // clear all active timers
      activeTimersRef.current.forEach(t => clearTimeout(t));
      activeTimersRef.current.clear();
      // clear dedupe timers
      dedupeTimersRef.current.forEach(t => clearTimeout(t));
      dedupeTimersRef.current.clear();
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (websocketRef.current) {
        try { websocketRef.current.close(); } catch (e) { }
        websocketRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch company settings on mount - gets active company (is_active=true) from database
  useEffect(() => {
    const fetchCompanySettings = async () => {
      try {
        // No company_id parameter - API returns the active company (is_active=true)
        const apiUrl = `${API_BASE_URL}/api/v1/settings/company`;
        // console.log('[WebView] Fetching company settings from:', apiUrl);
        
        const response = await fetch(apiUrl, { headers: buildWebviewFetchHeaders() });
        // console.log('[WebView] Company settings response status:', response.status);
        
        if (!response.ok) {
          console.error('[WebView] Failed to fetch company settings. Status:', response.status);
          return;
        }
        
        const data = await response.json();
        // console.log('[WebView] Fetched active company settings:', data);
        
        // Use values from API, only fall back to defaults if truly empty/null/undefined
        const logoValue = await getCompanyLogoSrc(
          resolveCompanyLogoSrc(data.logo, API_BASE_URL, data.company_id),
          buildWebviewFetchHeaders(),
        );
        const descriptionValue = data.description && data.description.trim() !== '' ? data.description : 'Welcome to Kivo.ai';
        
        setCompanySettings({
          logo: logoValue,
          description: descriptionValue,
        });
      } catch (error) {
        console.error('[WebView] Error fetching company settings:', error);
        // Keep using defaults on error
      }
    };

    fetchCompanySettings();
  }, []);

  // websocket connect (kept original, now pushes to showEntry)
  useEffect(() => {
    const connectWebsocket = () => {
      try {
        if (websocketRef.current) {
          websocketRef.current.close();
        }
        const wsUrl = buildWebviewWebSocketUrl(resolvedLaneNumber, false, resolvedCameraIds);
        console.log('[CUSTOM-WEBVIEW] ws connect', { wsUrl, filterIds: resolvedCameraIds });
        const socket = new WebSocket(wsUrl);
        websocketRef.current = socket;

        socket.onopen = () => {
          setIsSocketConnected(true);
        };

        socket.onmessage = (event) => {
          try {
            const payload = JSON.parse(event.data);
            if (!isAttendanceEvent(payload)) {
              return;
            }
            const entry = buildDisplayEntry(payload, { mode: 'attendance' });
            if (entry.mode !== 'attendance') {
              return;
            }
            if (!matchesLane(entry)) {
              return;
            }
            if (!matchesCamera(entry)) {
              return;
            }
            showEntry(entry);
          } catch (err) {
            console.error('[WebView WS] Failed to parse incoming message:', err);
          }
        };

        socket.onclose = () => {
          websocketRef.current = null;
          setIsSocketConnected(false);
          if (reconnectTimerRef.current) {
            clearTimeout(reconnectTimerRef.current);
          }
          reconnectTimerRef.current = setTimeout(connectWebsocket, 4000);
        };

        socket.onerror = (err) => {
          console.error('[WebView WS] Connection error:', err);
          setIsSocketConnected(false);
          try { socket.close(); } catch (e) { /* ignore */ }
        };
      } catch (error) {
        console.error('[WebView WS] Failed to initialise connection:', error);
      }
    };

    connectWebsocket();

    return () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
      if (websocketRef.current) {
        try { websocketRef.current.close(); } catch (e) { /* ignore */ }
        websocketRef.current = null;
      }
      setIsSocketConnected(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchesLane, matchesCamera, cameraFilterKey]);

  // Background styles
  const backgroundStyles = {
    position: 'fixed',
    top: '-50%',
    left: '-50%',
    width: '200%',
    height: '200%',
    background: `
      radial-gradient(circle at 20% 80%, rgba(64, 81, 138, 0.15) 0%, transparent 30%),
      radial-gradient(circle at 80% 20%, rgba(64, 81, 138, 0.10) 0%, transparent 30%),
      radial-gradient(circle at 40% 40%, rgba(64, 81, 138, 0.08) 0%, transparent 30%)
    `,
    animation: `${float} 20s ease-in-out infinite`,
    zIndex: -1,
  };

  // visibleSidebarEntries excludes any currently active on main screen
  const visibleSidebarEntries = sidebarEntries.filter(
    entry => !activeCards.some(c => c.id === entry.id)
  );
  const displayedSidebarEntries = visibleSidebarEntries.slice(0, visibleHistoryCount);
  const canShowMoreHistory = visibleSidebarEntries.length > 0 && (
    hasMoreHistory || visibleSidebarEntries.length > visibleHistoryCount
  );

  // Render helpers: fully responsive card for mobile, tablet, and laptop
  const renderOriginalCard = (entry, compact = false) => {
    const chipSx = {
      paddingTop: { xs: 0.2, sm: 0.3, md: 0.5 },
      height: { xs: 24, sm: 32, md: compact ? 34 : 40 },
      borderRadius: { xs: 1.5, md: 2 },
      textTransform: 'uppercase',
      fontWeight: 'bold',
      letterSpacing: { xs: 0.3, md: 0.8 },
      fontSize: { xs: '0.55rem', sm: '0.75rem', md: compact ? '0.9rem' : '1.2rem' },
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      '& .MuiChip-label': {
        px: { xs: 0.75, sm: 1, md: 2 },
        whiteSpace: 'nowrap',
        lineHeight: 1,
      },
    };

    return (
      <Card
        key={entry.id}
        sx={{
          width: '100%',
          height: '100%',
          maxWidth: '100%',
          mx: 0,
          px: { xs: 0.5, sm: 1, md: 1.5 },
          py: { xs: 0.5, sm: 1, md: 1.5 },
          borderRadius: { xs: 2, sm: 2.5, md: 3 },
          boxShadow: '0 10px 30px rgba(64, 81, 138, 0.2)',
          animation: `${slideIn} 0.5s ease`,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'stretch',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            transform: { xs: 'none', md: 'translateY(-2px)' },
            boxShadow: { xs: '0 10px 30px rgba(64, 81, 138, 0.2)', md: '0 12px 35px rgba(64, 81, 138, 0.25)' },
          }
        }}
      >
        {/* Left image section: Responsive width */}
        <Box sx={{
          width: { xs: '25%', sm: '22%', md: compact ? '24%' : '25%' },
          minWidth: { xs: '25%', sm: '22%', md: compact ? '24%' : '25%' },
          maxWidth: { xs: '25%', sm: '22%', md: compact ? '24%' : '25%' },
          background: 'linear-gradient(135deg, #40518A 0%, #5a6fa8 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}>
          {/* Image always displayed */}
          {entry.image ? (
            <Box
              component="img"
              src={entry.image}
              alt={entry.image}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center',
                display: 'block'
              }}
            />
          ) : (
            <Box sx={{ 
              width: '100%', 
              height: '100%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              bgcolor: 'primary.main' 
            }}>
              <Typography sx={{ 
                color: '#fff', 
                fontWeight: '700', 
                fontSize: { xs: '1rem', sm: '1.5rem', md: compact ? '1.8rem' : '3rem' }
              }}>
                {entry?.name?.split(' ').map(n => n[0]).join('')}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Right content section: Responsive padding and fonts */}
        <CardContent sx={{ 
          flex: 1, 
          p: { xs: 1, sm: 1.5, md: compact ? 1.5 : 2.1 },
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center',
          overflow: 'hidden',
          background: 'linear-gradient(to right, rgba(255,255,255,1) 0%, rgba(250,250,255,1) 100%)',
          minHeight: { xs: 'auto', md: compact ? 172 : 220 },
        }}>
          <Stack 
            direction="row" 
            spacing={{ xs: 0.5, sm: 0.75, md: 1 }}
            sx={{ 
              mb: { xs: 0.5, sm: 0.75, md: 1.1 },
              flexWrap: 'wrap', 
              alignItems: 'center', 
              gap: { xs: 0.5, sm: 0.75, md: 1 }
            }}
          >
            <Chip
              label={entry.actionLabel}
              color={entry.action === 'check_out' ? 'warning' : 'success'}
              size={isMobile ? 'small' : 'medium'}
              sx={chipSx}
            />
            {entry.directionText && (
              <Chip
                label={entry.directionText}
                color="info"
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  ...chipSx,
                  fontSize: { xs: '0.55rem', sm: '0.75rem', md: compact ? '0.9rem' : '1.1rem' },
                }}
              />
            )}
            {entry.isValidityExpired && (
                <Chip
                  label="Validity Expired"
                  size={isMobile ? 'small' : 'medium'}
                  color="error"
                  sx={{ ...chipSx, fontWeight: 'bold', borderRadius: { xs: 1.5, md: 2 } }}
                />
            )}
{entry.isLateEntry &&
  !snapshotIsVisitor(entry.employeeType) &&
  !snapshotIsVendor(entry.employeeType) && (
                  <Chip
                label="Late Entry"
                color="warning"
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  ...chipSx,
                  fontSize: { xs: '0.55rem', sm: '0.75rem', md: compact ? '0.9rem' : '1.1rem' },
                }}
              />
            )}
          </Stack>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 1 }, mb: { xs: 0.25, md: 0.5 } }}>
            <Typography
              variant="h2"
              sx={{
                flex: 1,
                minWidth: 0,
                fontSize: { xs: '1.15rem', sm: '1.45rem', md: compact ? '1.6rem' : '2.2rem', lg: compact ? '1.8rem' : '2.4rem' },
                fontWeight: '800',
                color: '#40518A',
                lineHeight: 1.15,
                letterSpacing: 0.2,
                wordBreak: 'break-word',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                textShadow: '0 1px 2px rgba(64, 81, 138, 0.1)'
              }}
            >
              {entry.name}
            </Typography>
            {snapshotIsVendor(entry.employeeType) && (
              <Tooltip title="Vendor" arrow>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.35, color: 'grey.700' }}>
                  <AssignmentIndIcon sx={{ fontSize: { xs: '0.9rem', md: compact ? '1.05rem' : '1.2rem' }, color: 'grey.700' }} />
                  <Typography
                    component="span"
                    sx={{
                      color: 'grey.700',
                      fontWeight: 700,
                      fontSize: { xs: '0.65rem', sm: '0.8rem', md: compact ? '0.9rem' : '1rem' },
                      lineHeight: 1,
                      textTransform: 'uppercase',
                      letterSpacing: 0.3,
                      display: { xs: 'none', sm: 'inline' },
                    }}
                  >
                    Vendor
                  </Typography>
                </Box>
              </Tooltip>
            )}
            {snapshotIsVisitor(entry.employeeType) && (
              <Tooltip title="Visitor" arrow>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.35, color: 'grey.700' }}>
                  <BadgeIcon sx={{ fontSize: { xs: '0.9rem', md: compact ? '1.05rem' : '1.2rem' }, color: 'grey.700' }} />
                  <Typography
                    component="span"
                    sx={{
                      color: 'grey.700',
                      fontWeight: 800,
                      fontSize: { xs: '0.6rem', sm: '0.68rem', md: compact ? '0.72rem' : '0.78rem' },
                      textTransform: 'uppercase',
                      letterSpacing: 0.3,
                      display: { xs: 'none', sm: 'inline' },
                    }}
                  >
                    Visitor
                  </Typography>
                </Box>
              </Tooltip>
            )}
          </Box>
          
          <Typography
            variant="body2"
            sx={{
              color: 'primary.main',
              mb: { xs: 0.35, sm: 0.5, md: 0.75 },
              fontWeight: 800,
              fontSize: { xs: '0.7rem', sm: '0.9rem', md: compact ? '1.05rem' : '1.35rem', lg: compact ? '1.15rem' : '1.45rem' },
              letterSpacing: 0.3
            }}
          >
             {entry.employeeCode }
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: '#40518A',
              mb: { xs: 0.25, sm: 0.4, md: 0.55 },
              fontWeight: 800,
              fontSize: { xs: '0.7rem', sm: '0.9rem', md: compact ? '1.08rem' : '1.4rem', lg: compact ? '1.15rem' : '1.5rem' },
              letterSpacing: 0.2,
              lineHeight: 1.25,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {entry.designation }
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: '#40518A',
              fontWeight: 800,
              fontSize: { xs: '0.7rem', sm: '0.9rem', md: compact ? '1.08rem' : '1.4rem', lg: compact ? '1.15rem' : '1.5rem' },
              letterSpacing: 0.2,
              lineHeight: 1.25,
              mb: { xs: 0.4, sm: 0.65, md: 0.95 },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {entry.department }
          </Typography>

          <Typography
            variant="h6"
            sx={{
              color: 'text.secondary',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 0.3, md: 0.5 },
              fontSize: { xs: '0.7rem', sm: '0.9rem', md: compact ? '1rem' : '1.3rem', lg: compact ? '1.1rem' : '1.4rem' },
              mt: { xs: 0.2, md: 0.45 },
              lineHeight: 1.2
            }}
          >
            🕐 {entry.time}
          </Typography>
        </CardContent>
      </Card>
    );
  };

  // Main render
  return (
    <Box sx={{
      minHeight: embedded ? '100%' : '100vh',
      height: embedded ? '100%' : 'auto',
      '@supports (height: 100dvh)': {
        minHeight: embedded ? '100%' : '100dvh',
      },
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
      color: '#40518A',
      position: 'relative',
      overflowX: 'hidden',
      overflowY: embedded ? { xs: 'auto', md: 'hidden' } : { xs: 'auto', md: 'hidden' },
      '&::before': backgroundStyles,
      '&::after': {
        content: '""',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 50% 50%, rgba(64, 81, 138, 0.1) 0%, transparent 70%)',
        pointerEvents: 'none',
        zIndex: 0
      }
    }}>
      {/* Main Container */}
      <Container
        maxWidth={embedded ? false : 'xl'}
        disableGutters={embedded}
        sx={{
          minHeight: embedded ? '100%' : '100vh',
          height: embedded ? '100%' : { xs: 'auto', md: '100vh' },
          '@supports (height: 100dvh)': {
            minHeight: embedded ? '100%' : '100dvh',
            height: embedded ? '100%' : { xs: 'auto', md: '100dvh' },
          },
          py: embedded ? 0 : { xs: 1, sm: 1.5, md: 2 },
          px: embedded ? 0 : undefined,
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Stack
          direction={isMobile ? 'column' : 'row'}
          spacing={embedded ? { xs: 0.75, md: 1 } : { xs: 1, sm: 1.5, md: 2 }}
          sx={{ height: { xs: embedded ? '100%' : 'auto', md: '100%' } }}
        >
          {/* Main Display Area */}
          <Paper
            elevation={24}
            sx={{
              display: embedded ? 'none' : 'flex',
              flex: 1,
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: { xs: 1.5, sm: 2, md: embedded ? 2 : 4 },
              p: embedded ? { xs: 0.75, sm: 1, md: 1.25 } : { xs: 1, sm: 1.5, md: 2 },
              position: 'relative',
              transition: 'all 0.5s ease',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(64, 81, 138, 0.03) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(64, 81, 138, 0.03) 0%, transparent 50%)',
                zIndex: 0,
              }
            }}
          >
            {/* Socket Connection Indicator - Responsive */}
            <Box
              sx={{
                position: 'absolute',
                bottom: { xs: 10, sm: 15, md: 20 },
                right: { xs: 10, sm: 15, md: 20 },
                width: { xs: 32, sm: 38, md: 45 },
                height: { xs: 32, sm: 38, md: 45 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                zIndex: 1000,
                border: '2px solid rgba(64, 81, 138, 0.1)',
              }}
            >
              <Box
                component="span"
                sx={{
                  width: { xs: 10, sm: 12, md: 14 },
                  height: { xs: 10, sm: 12, md: 14 },
                  borderRadius: '50%',
                  color: isSocketConnected ? '#2ecc71' : '#e74c3c',
                  backgroundColor: 'currentColor',
                  animation: `${connectionPulse} 1.8s ease-out infinite`,
                }}
                aria-label={isSocketConnected ? 'Socket connected' : 'Socket disconnected'}
              />
            </Box>

            {/* Speaker / audio unlock – top-right of main white panel */}
            <Tooltip
              title={
                !audioUnlocked
                  ? 'Tap once to enable attendance sounds in this browser'
                  : voicePlaying
                  ? 'Playing attendance sound…'
                  : 'Attendance sounds are on'
              }
              placement="left"
            >
              {/* Wrapper: speaker icon + ripple rings + muted slash overlay */}
              <Box
                onClick={!audioUnlocked ? handleAudioUnlockClick : undefined}
                aria-label={audioUnlocked ? 'Attendance sounds' : 'Enable attendance sounds'}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !audioUnlocked) handleAudioUnlockClick(); }}
                sx={{
                  position: 'absolute',
                  top: { xs: 10, sm: 15, md: 20 },
                  right: { xs: 10, sm: 15, md: 20 },
                  zIndex: 1000,
                  width: { xs: 36, sm: 38, md: 40 },
                  height: { xs: 36, sm: 38, md: 40 },
                  cursor: !audioUnlocked ? 'pointer' : 'default',
                }}
              >
                {/* Ripple rings — visible only while voice is playing */}
                {voicePlaying && [0, 0.5, 1].map((delay, i) => (
                  <Box
                    key={i}
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      borderRadius: '50%',
                      border: '2px solid',
                      borderColor: 'primary.main',
                      opacity: 0,
                      animation: `${rippleRing} 1.8s ease-out infinite`,
                      animationDelay: `${delay}s`,
                      pointerEvents: 'none',
                    }}
                  />
                ))}

                {/* Speaker icon base */}
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '50%',
                    bgcolor: 'rgba(255,255,255,0.95)',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                    border: '2px solid rgba(64, 81, 138, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'primary.main',
                  }}
                >
                  <VolumeUpIcon sx={{ fontSize: { xs: 18, sm: 20, md: 22 } }} />
                </Box>

                {/* Single black slash — muted state, only when locked (/) */}
                {!audioUnlocked && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'transparent',
                      pointerEvents: 'none',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        width: '58%',
                        height: 2,
                        bgcolor: 'rgba(0,0,0,0.60)',
                        borderRadius: 1,
                        transform: 'translate(-50%, -50%) rotate(45deg)',
                      },
                    }}
                  />
                )}
              </Box>
            </Tooltip>
            <Box sx={{ position: 'relative', zIndex: 1, width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
              {/* Page Header with Logo - Shows when cards are present - Responsive */}
              {(activeCards.length > 0 || currentEntry) && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: { xs: 8, sm: 12, md: 16 },
                    left: { xs: 8, sm: 12, md: 16 },
                    p: { xs: 0.75, sm: 1, md: 1.5 },
                    borderRadius: { xs: 1.5, md: 2 },
                    backdropFilter: 'blur(10px)',
                    animation: `${pulse} 2s infinite`,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    bgcolor: 'rgba(255,255,255,0.9)',
                  }}
                >
                {(companySettings === null) ? <Box
                    sx={{
                      width: "100%",
                      height: 120,
                      borderRadius: 2,
                      background:
                        "linear-gradient(90deg, #eeeeee 25%, #f8f8f8 50%, #eeeeee 75%)",
                      backgroundSize: "200% 100%",
                      animation: "shimmer 1.5s infinite linear",
                      "@keyframes shimmer": {
                        "0%": {
                          backgroundPosition: "200% 0",
                        },
                        "100%": {
                          backgroundPosition: "-200% 0",
                        },
                      },
                    }}
                  /> : <Box
                    component="img"
                    src={companySettings?.logo}
                    alt="Company Logo"
                    sx={{
                      height: { xs: 20, sm: 28, md: embedded ? 28 : 35 },
                      width: 'auto',
                      borderRadius: 1,
                      objectFit: 'contain'
                    }}
                  />}
                </Box>
              )}

              {/* Dynamic Attendance Cards Rendering */}
              {(() => {
                // Reverse the cards so newest (last added) appears first
                const cards = activeCards.map(c => c.original || c).reverse();
                const count = cards.length;
                const stackCardsOnNarrow = isMobile || isBelow800 || isShortScreen;

                // 0 cards → Welcome - Responsive
                if (count === 0) {
                  return (
                    <Box
                      sx={{
                        position: 'relative',
                        zIndex: 1,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}
                    >

                      {/* Always Visible Logo in Top-Left - Responsive */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: { xs: 10, sm: 15, md: 20 },
                          left: { xs: 10, sm: 15, md: 20 },
                          p: { xs: 0.75, sm: 1, md: 1.5 },
                        }}
                      >
                        {(companySettings === null) ? <Box
                          sx={{
                            width: "100%",
                            height: 120,
                            borderRadius: 2,
                            background:
                              "linear-gradient(90deg, #eeeeee 25%, #f8f8f8 50%, #eeeeee 75%)",
                            backgroundSize: "200% 100%",
                            animation: "shimmer 1.5s infinite linear",
                            "@keyframes shimmer": {
                              "0%": {
                                backgroundPosition: "200% 0",
                              },
                              "100%": {
                                backgroundPosition: "-200% 0",
                              },
                            },
                          }}
                        /> : <Box
                          component="img"
                          src={companySettings?.logo}
                          alt="Company Logo"
                          sx={{
                            height: { xs: 20, sm: 28, md: embedded ? 28 : 35 },
                            width: 'auto',
                            borderRadius: 1,
                            objectFit: 'contain'
                          }}
                      />}
                      </Box>

                      {/* Welcome Message - Responsive */}
                      <Box
                        sx={{
                          textAlign: 'center',
                          animation: `${pulse} 2s infinite`,
                          px: embedded ? { xs: 1, md: 2 } : { xs: 2, sm: 3, md: 4 }
                        }}
                      >
                        <Typography
                          variant="h1"
                          sx={{
                            fontSize: embedded
                              ? { xs: '1rem', sm: '1.25rem', md: '1.55rem', lg: '1.9rem' }
                              : { xs: '1.3rem', sm: '2rem', md: '3rem', lg: '3.8rem' },
                            color: '#40518A',
                            mb: embedded ? { xs: 0.5, md: 1 } : { xs: 1, md: 2 },
                            textShadow: '0 3px 15px rgba(64, 81, 138, 0.2)',
                            fontWeight: '900',
                            whiteSpace: 'pre-line',
                            letterSpacing: { xs: 0.5, md: 1 }
                          }}
                        >
                          {companySettings?.description}
                        </Typography>
                      </Box>

                    </Box>
                  );
                }
                
                
                // 1 card → center (responsive)
                if (count === 1) {
                  return (
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      justifyContent: 'center', 
                      alignItems: 'center',
                      width: '100%',
                      height: { xs: '25%', sm: '30%', md: isBelow1100 ? '38%' : '42%' },
                      px: { xs: 1, sm: 2, md: 3 }
                    }}>
                      <Box sx={{ 
                        width: { xs: '95%', sm: '90%', md: isBelow1300 ? '80%' : '70%' }, 
                        maxWidth: { xs: 500, sm: 700, md: 1000 }, 
                        height: '100%' 
                      }}>
                        {renderOriginalCard(cards[0])}
                      </Box>
                    </Box>
                  );
                }
                
                // 2 cards → side by side on desktop; vertical stack on narrow/short screens
                if (count === 2) {
                  if (stackCardsOnNarrow) {
                    return (
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        justifyContent: 'center',
                        alignItems: 'center', 
                        gap: { xs: 0.75, sm: 1, md: 1.5 },
                        width: '100%',
                        height: { xs: '50%', sm: '55%', md: '65%' },
                        px: { xs: 1, sm: 2, md: 3 },
                        py: { xs: 0.5, sm: 1, md: 1.5 }
                      }}>
                        {cards.slice(0, 2).map(entry => (
                          <Box key={entry.id} sx={{ 
                            width: { xs: '95%', sm: '90%', md: '85%' }, 
                            maxWidth: { xs: 500, sm: 700, md: 1000 }, 
                            height: 'calc(50% - 8px)' 
                          }}>
                            {renderOriginalCard(entry, true)}
                          </Box>
                        ))}
                      </Box>
                    );
                  }
                  return (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gridTemplateRows: '1fr',
                        columnGap: { sm: 1.5, md: 2 },
                        width: '100%',
                        height: { sm: '32%', md: isBelow1100 ? '36%' : '34%' },
                        px: { sm: 1, md: 1 },
                        py: { sm: 0.75, md: 1 },
                        alignItems: 'center',
                      }}
                    >
                      {cards.slice(0, 2).map(entry => (
                        <Box
                          key={entry.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            height: '100%',
                          }}
                        >
                          {renderOriginalCard(entry, true)}
                        </Box>
                      ))}
                    </Box>
                  );
                }

                // 3 cards → vertical stack (responsive)
                if (count === 3) {
                  return (
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      justifyContent: 'center',
                      alignItems: 'center', 
                      gap: { xs: 0.5, sm: 0.75, md: 1.5 }, 
                      width: '100%',
                      height: { xs: '85%', sm: '88%', md: '90%' },
                      px: { xs: 1, sm: 2, md: 3 },
                      py: { xs: 0.5, sm: 1, md: 1.5 }
                    }}>
                      {cards.map(entry => (
                        <Box key={entry.id} sx={{ 
                          width: { xs: '95%', sm: '92%', md: '88%' }, 
                          maxWidth: { xs: 500, sm: 700, md: 900 }, 
                          height: 'calc(33.33% - 8px)' 
                        }}>
                          {renderOriginalCard(entry, true)}
                        </Box>
                      ))}
                    </Box>
                  );
                }

                // 4 or more → 2x2 on desktop; vertical stack on narrow/short screens
                if (stackCardsOnNarrow) {
                  return (
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      justifyContent: 'center',
                      alignItems: 'center', 
                      gap: { xs: 0.4, sm: 0.6, md: 1 },
                      width: '100%',
                      height: { xs: '90%', sm: '92%', md: '94%' },
                      px: { xs: 1, sm: 1.5, md: 2 },
                      py: { xs: 0.4, sm: 0.75, md: 1 }
                    }}>
                      {cards.slice(0, 4).map(entry => (
                        <Box key={entry.id} sx={{ 
                          width: { xs: '95%', sm: '92%', md: '88%' }, 
                          maxWidth: { xs: 500, sm: 700, md: 900 }, 
                          height: 'calc(25% - 6px)' 
                        }}>
                          {renderOriginalCard(entry, true)}
                        </Box>
                      ))}
                    </Box>
                  );
                }
                return (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gridTemplateRows: '1fr 1fr',
                      columnGap: { sm: 1.5, md: 2 },
                      rowGap: { sm: 1.5, md: 2 },
                      width: '100%',
                      height: { sm: '60%', md: isBelow1100 ? '68%' : '64%' },
                      px: { sm: 0.5, md: 0.5 },
                      py: { sm: 0.5, md: 0.5 }
                    }}
                  >
                    {cards.slice(0, 4).map(entry => (
                      <Box key={entry.id} sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        height: '100%'
                      }}>
                        {renderOriginalCard(entry, true)}
                      </Box>
                    ))}
                  </Box>
                );
              })()}
            </Box>
          </Paper>

          {/* Notification Area - Always visible, 20-25% of display width with enhanced styling */}
          <Paper
            elevation={8}
            sx={{
              width: {
                xs: '100%',
                md: embedded ? '100%' : '25%',
                lg: embedded ? '100%' : '23%',
              },
              minWidth: embedded
                ? 0
                : { xs: 'auto', sm: 300, md: 340, lg: 380 },
              maxWidth: embedded
                ? 'none'
                : { xs: '100%', md: 420, lg: 480 },
              flex: embedded ? 1 : undefined,
              height: { xs: 'auto', md: embedded ? '100%' : undefined },
              minHeight: embedded ? { xs: 180, md: 0 } : undefined,
              maxHeight: { xs: embedded ? 'none' : '40vh', md: '100%' },
              p: embedded ? { xs: 1, md: 1.5 } : { xs: 1.5, sm: 2, md: 2.5 },
              borderRadius: { xs: 1.5, sm: 2, md: embedded ? 2 : 4 },
              background: 'linear-gradient(135deg, rgba(64, 81, 138, 0.95) 0%, rgba(90, 111, 168, 0.95) 100%)',
              backdropFilter: 'blur(15px)',
              overflow: 'auto',
              boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
              '&::-webkit-scrollbar': {
                width: { xs: '4px', md: '8px' },
              },
              '&::-webkit-scrollbar-track': {
                background: 'rgba(255,255,255,0.1)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(255,255,255,0.3)',
                borderRadius: '4px',
                '&:hover': {
                  background: 'rgba(255,255,255,0.4)',
                }
              }
            }}
          >
            <Stack spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
              {displayedSidebarEntries.map((entry) => (
                <Paper
                  key={entry.id}
                  elevation={6}
                  sx={{
                    p: { xs: 1.5, sm: 2, md: 2.5 },
                    borderRadius: { xs: 2, md: 3 },
                    borderLeft: `${isMobile ? '3px' : '5px'} solid ${entry.action === 'check_out'
                      ? theme.palette.warning.main
                      : theme.palette.success.main
                      }`,
                    animation: `${slideIn} 0.5s ease`,
                    background: 'rgba(255, 255, 255, 0.98)',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      transform: { xs: 'none', md: 'translateX(-5px)' },
                      boxShadow: { xs: 'none', md: '0 6px 20px rgba(0,0,0,0.15)' },
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, md: 2 }, mb: { xs: 1, md: 1.8 } }}>
                    <Box
                      sx={{
                        width: { xs: 50, sm: 60, md: 70, lg: 88 },
                        aspectRatio: '2.5 / 4',
                        borderRadius: { xs: 1.5, md: 2 },
                        overflow: 'hidden',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        backgroundColor: 'grey.200',
                        border: '2px solid rgba(64, 81, 138, 0.1)',
                      }}
                    >
                      {entry.image ? (
                        <Box
                          component="img"
                          src={entry.image}
                          alt={entry?.name}
                          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <Box sx={{ 
                          width: '100%', 
                          height: '100%', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          bgcolor: 'primary.main' 
                        }}>
                          <Typography sx={{ 
                            color: '#fff', 
                            fontWeight: '700', 
                            fontSize: { xs: '1rem', sm: '1.5rem', md: '2rem', lg: '2.5rem' }
                          }}>
                            {entry?.name?.split(' ').map(n => n[0]).join('')}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 1 } }}>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            flex: 1,
                            minWidth: 0,
                            fontSize: { xs: '0.95rem', sm: '1.2rem', md: '1.35rem', lg: '1.45rem' }, 
                            fontWeight: '800',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: '#2c3e50'
                          }}
                        >
                          {entry.name}
                        </Typography>
                        {snapshotIsVendor(entry.employeeType) ? (
                          <Tooltip title="Vendor" arrow>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.35, color: 'grey.700', }}>
                              <AssignmentIndIcon sx={{ fontSize: { xs: '0.8rem', md: '0.92rem' }, color: 'grey.700' }} />
                              <Typography
                                component="span"
                                sx={{
                                  color: 'grey.700',
                                  fontWeight: 700,
                                  fontSize: { xs: '0.6rem', md: '0.72rem' },
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.28,
                                  display: { xs: 'none', sm: 'inline' },
                                }}
                              >
                                Vendor
                              </Typography>
                            </Box>
                          </Tooltip>
                        ) : null}
                        {snapshotIsVisitor(entry.employeeType) ? (
                          <Tooltip title="Visitor" arrow>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.35, color: 'grey.700', }}>
                              <BadgeIcon sx={{ fontSize: { xs: '0.8rem', md: '0.92rem' }, color: 'grey.700' }} />
                              <Typography
                                component="span"
                                sx={{
                                  color: 'grey.700',
                                  fontWeight: 800,
                                  fontSize: { xs: '0.55rem', md: '0.62rem' },
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.28,
                                  display: { xs: 'none', sm: 'inline' },
                                }}
                              >
                                Visitor
                              </Typography>
                            </Box>
                          </Tooltip>
                        ) : null}
                      </Box>
                      {entry.employeeCode && (
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            color: 'primary.main', 
                            fontWeight: 700, 
                            fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem', lg: '1.05rem' },
                            mb: { xs: 0.15, md: 0.3 }
                          }}
                        >
                           {entry.employeeCode }
                        </Typography>
                      )}
                      {entry.isValidityExpired && (
                        <Chip
                          label="Validity Expired"
                          size="small"
                          color="error"
                          sx={{
                            fontWeight: 'bold',
                            borderRadius: 1,
                            mb: 0.5,
                            height: { xs: 20, sm: 22, md: 24 },
                            '& .MuiChip-label': { fontSize: { xs: '0.55rem', sm: '0.65rem', md: '0.7rem' } },
                          }}
                        />
                      )}
{entry.isLateEntry &&
  !snapshotIsVisitor(entry.employeeType) &&
  !snapshotIsVendor(entry.employeeType) && (
                            <Chip
                          label="Late Entry"
                          size="small"
                          color="warning"
                          sx={{
                            fontWeight: 'bold',
                            borderRadius: 1,
                            mb: 0.5,
                            mr: 0.5,
                            height: { xs: 20, sm: 22, md: 24 },
                            '& .MuiChip-label': { fontSize: { xs: '0.55rem', sm: '0.65rem', md: '0.7rem' } },
                          }}
                        />
                      )}
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#40518A',
                          fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem', lg: '1rem' },
                          fontWeight: 400,
                          lineHeight: 1.25,
                          mb: 0.2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                         {entry.designation }
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#40518A',
                          fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem', lg: '1rem' },
                          fontWeight: 400,
                          lineHeight: 1.25,
                          mb: 0.3,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                       {entry.department }
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            color: 'primary.main',
                            fontSize: { xs: '0.7rem', sm: '0.85rem', md: '0.95rem', lg: '1.05rem' },
                            fontWeight: 500
                          }}
                        >
                          {entry.time}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Stack
                    direction="row"
                    spacing={{ xs: 0.5, sm: 0.75, md: 1 }}
                    sx={{
                      flexWrap: 'wrap',
                      width: '100%',
                      rowGap: { xs: 0.5, md: 0.75 },
                      gap: { xs: 0.5, md: 0.75 },
                    }}
                  >
                    <Chip
                      label={entry.actionLabel}
                      color={entry.action === 'check_out' ? 'warning' : 'success'}
                      size={isMobile ? 'small' : 'medium'}
                      sx={{
                        paddingTop: { xs: 0, md: 0.3 },
                        height: { xs: 22, sm: 28, md: 36 },
                        flex: '1 1 0',
                        minWidth: 0,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        fontWeight: 800,
                        '& .MuiChip-label': {
                          fontSize: { xs: '0.55rem', sm: '0.7rem', md: '0.9rem', lg: '1rem' },
                          fontWeight: 800,
                          textTransform: 'uppercase',
                          px: { xs: 0.5, sm: 0.75, md: 1.1 },
                          letterSpacing: 0.3,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }
                      }}
                    />
                    <Chip
                      label={entry.directionText}
                      color="info"
                      size={isMobile ? 'small' : 'medium'}
                      sx={{
                        paddingTop: { xs: 0, md: 0.3 },
                        height: { xs: 22, sm: 28, md: 36 },
                        flex: '1 1 0',
                        minWidth: 0,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        fontWeight: 800,
                        '& .MuiChip-label': {
                          fontSize: { xs: '0.55rem', sm: '0.7rem', md: '0.9rem', lg: '1rem' },
                          fontWeight: 800,
                          textTransform: 'uppercase',
                          px: { xs: 0.5, sm: 0.75, md: 1.1 },
                          letterSpacing: 0.3,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }
                      }}
                    />
                  </Stack>
                </Paper>
              ))}
              {canShowMoreHistory && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', pt: 0.5, pb: 1 }}>
                <Button
                   variant="outlined"
                  onClick={handleShowMoreHistory}
                  disabled={isHistoryLoading}
                  sx={{
                    color: 'white',
                    borderColor: 'rgba(255, 255, 255, 0.55)',
                    textTransform: 'none',
                    fontWeight: 600,
                    px: { xs: 1.5, md: 2.5 },
                    fontSize: { xs: '0.8rem', md: '0.95rem' },
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    },
                    '&.Mui-disabled': {
                      color: 'rgba(255, 255, 255, 0.55)',
                      borderColor: 'rgba(255, 255, 255, 0.35)',
                    },
                  }}
                >
                  {isHistoryLoading ? 'Loading...' : 'Show More'}
                </Button>
                </Box>
              )}
              {visibleSidebarEntries.length === 0 && !isHistoryLoading && (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: { xs: 4, sm: 6, md: 8 },
                  px: { xs: 1, md: 2 }
                }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: 'white', 
                      fontSize: { xs: '0.9rem', sm: '1rem', md: '1.15rem' },
                      fontWeight: 600,
                      opacity: 0.9,
                      mb: { xs: 0.5, md: 1 }
                    }}
                  >
                    No Recent Activity
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'rgba(255,255,255,0.7)', 
                      fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem' }
                    }}
                  >
                    Attendance logs will appear here
                  </Typography>
                </Box>
              )}
            </Stack>
          </Paper>
        </Stack>
      </Container>

      {/* floating FAB removed – speaker control is now in the sidebar header */}

    </Box>
  );
};

export default WebView;
